import {
  AssetType,
  AttributionMethodType,
  AttributionTimeWindow,
  AudienceSplit,
  Column,
  ColumnReference,
  ColumnType,
  ColumnValue,
  EventTraitColumn,
  InlineAggregatedTrait,
  InlineFormulaTrait,
  InteractionLookbackWindow,
  JourneyEventColumn,
  InteractionType,
  RawColumn,
  RelatedColumn,
  MergedColumn,
  JourneyMemberStateColumn,
  SplitSamplingType,
  SyntheticColumn,
  StaticValueColumn,
  TraitColumn,
  TransformedColumn,
  ValueType,
  getPropertyType,
  isColumnTypeValue,
  isInlineTraitColumn,
  isJourneyEventColumn,
  isRelatedJourneyEventColumn,
  isMergedColumn,
  isRawColumn,
  isRelatedColumn,
  isTraitColumn,
  isJourneyMemberStateColumn,
  isTransformedColumn,
  isStaticValueColumn,
  isSyntheticColumn,
  isSyntheticUserDefinedMetricConfig,
  DecisionEngineAnalyticsCampaignMetricType,
  DecisionEngineIncrementalityMetricsDefinition,
  DecisionEngineAttributionMetricsDefinition,
  DecisionEngineAnalyticsMetricDefinition,
  NormalizationType,
  isDecisionEngineInteractionsMetricDefinition,
  SyntheticMetricType,
  TAG_FEATURE_PREFIX,
} from "@hightouch/lib/query/visual/types";
import { OrderByType } from "@hightouch/lib/query/visual/types/filter";
import {
  AndCondition,
  AndOrCondition,
  Condition,
  ConditionType,
  EventCondition,
  FunnelCondition,
  JourneySetCondition,
  NumberOfCondition,
  OrCondition,
  PropertyCondition,
  ReferencedPropertyCondition,
  RootCondition,
  SegmentSetCondition,
  NestableCondition,
  TimestampGranularity,
  TimeType,
  Window,
} from "@hightouch/lib/query/visual/types/condition";
import {
  AttributionBasis,
  AttributionWindow,
  AudienceAggregationType,
  Goal,
  GoalConfig,
  UserDefinedMetricConfig,
  PerUserAggregationType,
  PredefinedMetric,
  SumMetricConfig,
} from "@hightouch/lib/query/visual/types/goals";
import {
  IntervalUnit,
  RelativeDirection,
} from "@hightouch/lib/query/visual/types/interval";
import {
  BooleanOperator,
  JsonArrayObjectOperator,
  JsonArrayOperator,
  JsonOperator,
  NumberOperator,
  Operator,
  StringOperator,
  TimestampOperator,
  UnknownOperator,
} from "@hightouch/lib/query/visual/types/operator";
import {
  RawSqlTraitConfig,
  TraitType,
  TraitConfig,
} from "@hightouch/lib/query/visual/types/trait-definitions";

import {
  AudienceParentFragment,
  AudienceQuery,
  FilterableColumnFragment,
  RelationshipFragment,
  TraitDefinitionFragment,
} from "src/graphql";
import { Option } from "src/ui/select";

type Audience = AudienceQuery["segments_by_pk"];

type NonNullableAudience = NonNullable<Audience>;

export {
  getTraitColumnType,
  visitConditionList,
} from "@hightouch/lib/query/visual/util";
export { ModelSemanticType } from "@hightouch/lib/query/visual/semantic-type";
export { AnalyticsFrequency } from "@hightouch/lib/query/visual/types/analytics";
export type { AnalyticsMetricDefinition } from "@hightouch/lib/query/visual/types/analytics";
export type {
  SizeCap,
  SizeCapLimitType,
  SplitTestDefinition,
  VisualQueryFilter,
  AudienceTemplateDefinition,
} from "@hightouch/lib/query/visual/types/filter";
export { IntervalUnit } from "@hightouch/lib/query/visual/types/interval";
export type { IntervalValue } from "@hightouch/lib/query/visual/types/interval";
export type { Operator } from "@hightouch/lib/query/visual/types/operator";
export type { TimeRangeValue } from "@hightouch/lib/query/visual/types/time-range";
export {
  AssetType,
  AttributionBasis,
  AttributionMethodType,
  AttributionTimeWindow,
  AudienceAggregationType,
  BooleanOperator,
  ColumnType,
  ConditionType,
  DecisionEngineAnalyticsCampaignMetricType,
  InteractionType,
  OrderByType,
  getPropertyType,
  isColumnTypeValue,
  isDecisionEngineInteractionsMetricDefinition,
  isSyntheticUserDefinedMetricConfig,
  NormalizationType,
  NumberOperator,
  PerUserAggregationType,
  PredefinedMetric,
  RelativeDirection,
  SplitSamplingType,
  StringOperator,
  UnknownOperator,
  SyntheticMetricType,
  TAG_FEATURE_PREFIX,
  TimeType,
  TimestampOperator,
  TimestampGranularity,
  TraitType,
  ValueType,
};
export type {
  AndCondition,
  AndOrCondition,
  AttributionWindow,
  Audience,
  AudienceParentFragment as AudienceParent,
  AudienceSplit,
  Column,
  ColumnReference,
  ColumnValue,
  Condition,
  DecisionEngineAttributionMetricsDefinition,
  DecisionEngineAnalyticsMetricDefinition,
  DecisionEngineIncrementalityMetricsDefinition,
  EventCondition,
  EventTraitColumn,
  FilterableColumnFragment as FilterableColumn,
  FunnelCondition,
  Goal,
  GoalConfig,
  UserDefinedMetricConfig,
  InteractionLookbackWindow,
  MergedColumn,
  JourneyMemberStateColumn,
  NonNullableAudience,
  NumberOfCondition,
  OrCondition,
  PropertyCondition,
  RawColumn,
  RawSqlTraitConfig,
  ReferencedPropertyCondition,
  RelatedColumn,
  JourneyEventColumn,
  RelationshipFragment as Relationship,
  RootCondition,
  SegmentSetCondition,
  JourneySetCondition,
  NestableCondition,
  SumMetricConfig,
  SyntheticColumn,
  StaticValueColumn,
  TraitColumn,
  TraitConfig,
  TraitDefinitionFragment as TraitDefinition,
  TransformedColumn,
  Window,
};
export const ColumnDateTypes = [ColumnType.Date, ColumnType.Timestamp];

interface OperatorOption extends Option {
  isSourceSupported?: (
    connectionType?: string,
    isJsonPathColumn?: boolean,
  ) => boolean;
}

export const filterSupportedOperators = (
  connectionType?: string,
  isJsonPathColumn?: boolean,
) => {
  return (option: OperatorOption) =>
    !("isSourceSupported" in option && option.isSourceSupported) ||
    option.isSourceSupported(connectionType, isJsonPathColumn);
};

const operatorToLabel = (operator: string) => {
  return operator
    .replace(/([A-Z])/g, " $1")
    .toLowerCase()
    .trim();
};

/**
 * JSON subpath filtering has an edge case: on BigQuery casting and comparison to null doesn't work.
 * So we remove exists/not exists operators from all column types except Unknown, which does work.
 */
const existsOperatorKeys = ["Exists", "ExistsAndIsNotEmpty", "DoesNotExist"];
const bigQueryJsonNotSupported = (
  connectionType?: string,
  isJsonPathColumn?: boolean,
) => !isJsonPathColumn || connectionType !== "bigquery";

const makeStringOperatorOption = (key: string): OperatorOption => ({
  label: operatorToLabel(key),
  value: StringOperator[key],
  isSourceSupported: existsOperatorKeys.includes(key)
    ? bigQueryJsonNotSupported
    : undefined,
});

// IsIn / NotIsIn aren't compiled by the main audience query compiler
// (packages/backend/lib/query/visual/property-condition.ts throws on them).
// Use stringOperatorOptionsWithIsIn / OperatorOptionsWithIsIn in contexts whose
// compilers do support them: trait catalog filters, predictive traits, and
// Decision Engine feature matrices.
const IS_IN_STRING_OPERATORS = new Set<string>([
  StringOperator.IsIn,
  StringOperator.NotIsIn,
]);

export const stringOperatorOptions = Object.keys(StringOperator)
  .map(makeStringOperatorOption)
  .filter((opt) => !IS_IN_STRING_OPERATORS.has(opt.value));

export const stringOperatorOptionsWithIsIn = Object.keys(StringOperator).map(
  makeStringOperatorOption,
);

export const numberOperatorOptions = Object.keys(NumberOperator).map((key) => ({
  label: operatorToLabel(key),
  value: NumberOperator[key],
  isSourceSupported: existsOperatorKeys.includes(key)
    ? bigQueryJsonNotSupported
    : undefined,
}));

export const numberOfOperatorOptions: {
  label: string;
  value: NumberOperator;
}[] = [
  {
    label: "exactly",
    value: NumberOperator.Equals,
  },
  {
    label: "not",
    value: NumberOperator.DoesNotEqual,
  },
  { label: "greater than", value: NumberOperator.GreaterThan },
  { label: "less than", value: NumberOperator.LessThan },
  {
    label: "greater than or equal to",
    value: NumberOperator.GreaterThanOrEqualTo,
  },
  { label: "less than or equal to", value: NumberOperator.LessThanOrEqualTo },
];

export const numberOperatorToLabel: Record<NumberOperator, string> = {
  [NumberOperator.Equals]: "exactly",
  [NumberOperator.DoesNotEqual]: "not",
  [NumberOperator.GreaterThan]: "greater than",
  [NumberOperator.LessThan]: "less than",
  [NumberOperator.GreaterThanOrEqualTo]: "greater than or equal to",
  [NumberOperator.LessThanOrEqualTo]: "less than or equal to",
  [NumberOperator.Exists]: "exists",
  [NumberOperator.DoesNotExist]: "exists",
  [NumberOperator.Between]: "between",
} as const;

export const eventOperatorOptions = [
  {
    label: "at least",
    value: NumberOperator.GreaterThanOrEqualTo,
  },
  { label: "at most", value: NumberOperator.LessThanOrEqualTo },
  { label: "exactly", value: NumberOperator.Equals },
];

export const eventOperatorToLabel = {
  [NumberOperator.GreaterThanOrEqualTo]: "at least",
  [NumberOperator.LessThanOrEqualTo]: "at most",
  [NumberOperator.Equals]: "exactly",
} as const;

export const segmentSetOperatorOptions = [
  {
    label: "Are included in",
    value: true,
  },
  {
    label: "Are not included in",
    value: false,
  },
];

export const journeySetOperatorOptions: {
  label: string;
  value: JourneySetCondition["filter"]["condition"];
  description?: string;
}[] = [
  {
    label: "Have entered",
    value: "entered-journey",
    description: "Current and past members",
  },
  {
    label: "Have not entered",
    value: "not-entered-journey",
  },
  {
    label: "Are currently in",
    value: "in-journey",
  },
  {
    label: "Are not currently in",
    value: "not-in-journey",
    description: "May have entered previously",
  },
  {
    label: "Have completed",
    value: "completed-journey",
  },
  {
    label: "Have not completed",
    value: "not-completed-journey",
  },
  {
    label: "Have exited",
    value: "exited-journey",
    description: "Met exit criteria before completing the journey",
  },
  {
    label: "Have not exited",
    value: "not-exited-journey",
  },
];

export const journeySetWindowTimestampOperatorOptions = [
  {
    value: "any_time",
    label: "any time",
  },
  {
    value: TimestampOperator.Equals,
    label: "on",
  },
  {
    value: TimestampOperator.Before,
    label: "before",
  },
  {
    value: TimestampOperator.After,
    label: "after",
  },
  {
    value: TimestampOperator.Within,
    label: "within",
  },
  {
    value: TimestampOperator.NotWithin,
    label: "not within",
  },
  {
    value: TimestampOperator.BetweenInclusive,
    label: "between",
  },
  {
    value: TimestampOperator.Between,
    label: "between (exclusive)",
  },
];

export const journeySetWindowTimestampOperatorOptionsLabelMap: Map<
  string,
  string
> = new Map(
  [
    ...journeySetWindowTimestampOperatorOptions,
    { value: "any_time", label: "any time" },
  ].map((option) => [option.value, option.label]),
);

export const timestampOperatorOptions = [
  { label: "equals", value: TimestampOperator.Equals },
  { label: "before", value: TimestampOperator.Before },
  { label: "after", value: TimestampOperator.After },
  { label: "within", value: TimestampOperator.Within },
  { label: "not within", value: TimestampOperator.NotWithin },
  {
    label: "exists",
    value: TimestampOperator.Exists,
    isSourceSupported: bigQueryJsonNotSupported,
  },
  {
    label: "does not exist",
    value: TimestampOperator.DoesNotExist,
    isSourceSupported: bigQueryJsonNotSupported,
  },
  { label: "anniversary", value: TimestampOperator.Anniversary },
  { label: "between", value: TimestampOperator.BetweenInclusive },
  { label: "between (exclusive)", value: TimestampOperator.Between },
];

export const windowOperatorOptions = [
  {
    value: TimestampOperator.Within,
    label: "within",
  },
  {
    value: TimestampOperator.NotWithin,
    label: "not within",
  },
  {
    value: TimestampOperator.BetweenInclusive,
    label: "between",
  },
  {
    value: TimestampOperator.Between,
    label: "between (exclusive)",
  },
  {
    value: TimestampOperator.After,
    label: "after",
  },
  {
    value: TimestampOperator.Before,
    label: "before",
  },
];

export const funnelWindowOperatorOptions = [
  {
    value: TimestampOperator.Within,
    label: "within",
  },
  {
    value: TimestampOperator.NotWithin,
    label: "not within",
  },
  {
    value: TimestampOperator.BetweenInclusive,
    label: "between",
  },
  {
    value: TimestampOperator.Between,
    label: "between (exclusive)",
  },
];

export const timestampSuggestionsOperatorOptions = [
  { label: "before", value: TimestampOperator.Before },
  { label: "after", value: TimestampOperator.After },
  {
    label: "exists",
    value: TimestampOperator.Exists,
    isSourceSupported: bigQueryJsonNotSupported,
  },
  {
    label: "does not exist",
    value: TimestampOperator.DoesNotExist,
    isSourceSupported: bigQueryJsonNotSupported,
  },
  { label: "anniversary", value: TimestampOperator.Anniversary },
  { label: "between", value: TimestampOperator.Between },
];

export const booleanOperatorOptions = [
  { label: "equals", value: BooleanOperator.Equals },
  { label: "does not equal", value: BooleanOperator.DoesNotEqual },
  {
    label: "exists",
    value: BooleanOperator.Exists,
    isSourceSupported: bigQueryJsonNotSupported,
  },
  {
    label: "does not exist",
    value: BooleanOperator.DoesNotExist,
    isSourceSupported: bigQueryJsonNotSupported,
  },
];

export const jsonOperatorOptions = [
  { label: "exists", value: JsonOperator.Exists },
  { label: "does not exist", value: JsonOperator.DoesNotExist },
];

export const unknownOperatorOptions = [
  { label: "exists", value: UnknownOperator.Exists },
  { label: "does not exist", value: JsonOperator.DoesNotExist },
];

export const jsonArrayObjectOperatorOptions = [
  { label: "any item", value: JsonArrayObjectOperator.Any },
  { label: "all items", value: JsonArrayObjectOperator.All },
];

export const jsonArrayNumbersOperatorOptions = [
  {
    label: "array contains",
    description: "array contains one of the input values",
    value: JsonArrayOperator.Contains,
  },
  {
    label: "array does not contain",
    description: "array does not contain any of the input values",
    value: JsonArrayOperator.DoesNotContain,
  },
  {
    label: "array is not empty",
    description: "array contains at least one non-null value",
    value: JsonArrayOperator.ContainsNonNull,
    isSourceSupported: (connectionType?: string) =>
      !!connectionType &&
      ["postgres", "snowflake", "bigquery", "databricks"].includes(
        connectionType,
      ),
  },
];
export const jsonArrayStringsOperatorOptions = [
  {
    label: "array contains (exact)",
    description:
      "array contains an item which exactly matches one of the input values",
    value: JsonArrayOperator.Contains,
  },
  {
    label: "array does not contain (exact)",
    description:
      "array does not contain any items which exactly match any of the input values",
    value: JsonArrayOperator.DoesNotContain,
  },
  {
    label: "array contains (partial)",
    description:
      "array contains an item which partially matches one of the input values",
    value: JsonArrayOperator.ContainsLike,
    isSourceSupported: (connectionType?: string) =>
      !!connectionType &&
      ["postgres", "snowflake", "bigquery"].includes(connectionType),
  },
  {
    label: "array does not contain (partial)",
    description:
      "array does not contain any items which partially match any of the input values",
    value: JsonArrayOperator.DoesNotContainLike,
    isSourceSupported: (connectionType?: string) =>
      !!connectionType &&
      ["postgres", "snowflake", "bigquery"].includes(connectionType),
  },
  {
    label: "array is not empty",
    description: "array contains at least one non-null value",
    value: JsonArrayOperator.ContainsNonNull,
    isSourceSupported: (connectionType?: string) =>
      !!connectionType &&
      ["postgres", "snowflake", "bigquery", "databricks"].includes(
        connectionType,
      ),
  },
];

export enum CalendarIntervalUnit {
  Day = "calendar day",
  Month = "calendar month",
  Year = "calendar year",
}

type IntervalUnitsThatSupportCalendarEquivalents =
  | IntervalUnit.Day
  | IntervalUnit.Month
  | IntervalUnit.Year;

export const intervalUnitSupportsCalendarEquivalent = (
  unit?: IntervalUnit,
): unit is IntervalUnitsThatSupportCalendarEquivalents =>
  !!unit &&
  [IntervalUnit.Day, IntervalUnit.Month, IntervalUnit.Year].includes(unit);

export const getMatchingGranularity = (
  interval: IntervalUnitsThatSupportCalendarEquivalents,
) => {
  switch (interval) {
    case IntervalUnit.Day:
      return TimestampGranularity.Day;
    case IntervalUnit.Month:
      return TimestampGranularity.Month;
    case IntervalUnit.Year:
      return TimestampGranularity.Year;
    default:
      return TimestampGranularity.Full;
  }
};

export const CalendarIntervalOptions = [
  {
    value: CalendarIntervalUnit.Day,
    label: "calendar day(s)",
  },
  {
    value: CalendarIntervalUnit.Month,
    label: "calendar month(s)",
  },
  {
    value: CalendarIntervalUnit.Year,
    label: "calendar year(s)",
  },
];

export const CalendarIntervalOptionsSingular = [
  {
    value: CalendarIntervalUnit.Day,
    label: "calendar day",
  },
  {
    value: CalendarIntervalUnit.Month,
    label: "calendar month",
  },
  {
    value: CalendarIntervalUnit.Year,
    label: "calendar year",
  },
];

export const DateIntervalOptions = [
  {
    value: IntervalUnit.Day,
    label: "day(s)",
  },
  {
    value: IntervalUnit.Week,
    label: "week(s)",
  },
  {
    value: IntervalUnit.Month,
    label: "month(s)",
  },
  {
    value: IntervalUnit.Year,
    label: "year(s)",
  },
  ...CalendarIntervalOptions,
];

export const isCalendarInterval = (
  interval: string,
): interval is CalendarIntervalUnit => {
  return interval.startsWith("calendar");
};

export const IntervalOptions = [
  {
    value: IntervalUnit.Minute,
    label: "minute(s)",
  },
  {
    value: IntervalUnit.Hour,
    label: "hour(s)",
  },
  ...DateIntervalOptions,
];

export const OperatorOptions: Record<ColumnType, Array<OperatorOption>> = {
  [ColumnType.Boolean]: booleanOperatorOptions,
  [ColumnType.Number]: numberOperatorOptions,
  [ColumnType.BigInt]: numberOperatorOptions,
  [ColumnType.String]: stringOperatorOptions,
  [ColumnType.Timestamp]: timestampOperatorOptions,
  [ColumnType.Date]: timestampOperatorOptions,
  [ColumnType.JsonArrayStrings]: jsonArrayStringsOperatorOptions,
  [ColumnType.JsonArrayNumbers]: jsonArrayNumbersOperatorOptions,
  [ColumnType.JsonArrayObjects]: jsonArrayObjectOperatorOptions,
  [ColumnType.Json]: jsonOperatorOptions,
  // Supported only in json filters
  [ColumnType.Unknown]: unknownOperatorOptions,
  // Not supported
  [ColumnType.Null]: stringOperatorOptions,
};

export const OperatorOptionsWithIsIn: Record<
  ColumnType,
  Array<OperatorOption>
> = {
  ...OperatorOptions,
  [ColumnType.String]: stringOperatorOptionsWithIsIn,
  [ColumnType.Null]: stringOperatorOptionsWithIsIn,
};

const referenceStringOperatorOptions = [
  {
    label: "equals",
    value: StringOperator.Equals,
  },
  {
    label: "does not equal",
    value: StringOperator.DoesNotEqual,
  },
];

const referenceTimestampOperatorOptions = [
  { label: "before", value: TimestampOperator.Before },
  { label: "before (inclusive)", value: TimestampOperator.BeforeInclusive },
  { label: "after", value: TimestampOperator.After },
  { label: "after (inclusive)", value: TimestampOperator.AfterInclusive },
];

export const ReferencePropertyOperatorOptions: Record<
  ColumnType,
  Array<OperatorOption>
> = {
  [ColumnType.Boolean]: [
    { label: "equals", value: BooleanOperator.Equals },
    { label: "does not equal", value: BooleanOperator.DoesNotEqual },
  ],
  [ColumnType.Number]: numberOperatorOptions,
  [ColumnType.BigInt]: numberOperatorOptions,
  [ColumnType.String]: referenceStringOperatorOptions,
  [ColumnType.Timestamp]: referenceTimestampOperatorOptions,
  [ColumnType.Date]: referenceTimestampOperatorOptions,
  // Not supported
  [ColumnType.JsonArrayStrings]: jsonArrayStringsOperatorOptions,
  [ColumnType.JsonArrayNumbers]: jsonArrayNumbersOperatorOptions,
  [ColumnType.JsonArrayObjects]: jsonArrayObjectOperatorOptions,
  [ColumnType.Unknown]: unknownOperatorOptions,
  [ColumnType.Json]: referenceStringOperatorOptions,
  [ColumnType.Null]: referenceStringOperatorOptions,
};

export const ReferencePropertyDefaultOperators: Record<ColumnType, string> = {
  [ColumnType.Boolean]: BooleanOperator.Equals,
  [ColumnType.Number]: NumberOperator.Equals,
  [ColumnType.BigInt]: NumberOperator.Equals,
  [ColumnType.String]: StringOperator.Equals,
  [ColumnType.Timestamp]: TimestampOperator.Before,
  [ColumnType.Date]: TimestampOperator.Before,
  // Not supported
  [ColumnType.JsonArrayStrings]: JsonArrayOperator.Contains,
  [ColumnType.JsonArrayNumbers]: JsonArrayOperator.Contains,
  [ColumnType.JsonArrayObjects]: JsonArrayObjectOperator.Any,
  [ColumnType.Unknown]: UnknownOperator.Exists,
  [ColumnType.Json]: StringOperator.Equals,
  [ColumnType.Null]: StringOperator.Equals,
};

export const DefaultOperators: Record<ColumnType, string> = {
  [ColumnType.Boolean]: BooleanOperator.Equals,
  [ColumnType.Number]: NumberOperator.Equals,
  [ColumnType.BigInt]: NumberOperator.Equals,
  [ColumnType.String]: StringOperator.Equals,
  [ColumnType.Timestamp]: TimestampOperator.Within,
  [ColumnType.Date]: TimestampOperator.Within,
  [ColumnType.JsonArrayStrings]: JsonArrayOperator.Contains,
  [ColumnType.JsonArrayNumbers]: JsonArrayOperator.Contains,
  [ColumnType.JsonArrayObjects]: JsonArrayObjectOperator.Any,
  [ColumnType.Json]: JsonOperator.Exists,
  [ColumnType.Unknown]: UnknownOperator.Exists,
  // Not supported
  [ColumnType.Null]: StringOperator.Equals,
};

export const OperatorsWithoutValue = [
  StringOperator.Exists,
  StringOperator.ExistsAndIsNotEmpty,
  NumberOperator.Exists,
  TimestampOperator.Exists,
  TimestampOperator.DoesNotExist,
  TimestampOperator.Anniversary,
  JsonArrayOperator.ContainsNonNull,
  JsonArrayObjectOperator.Any,
  JsonArrayObjectOperator.All,
];

export const IntervalOperators = [
  TimestampOperator.Within,
  TimestampOperator.NotWithin,
  TimestampOperator.Between,
  TimestampOperator.BetweenInclusive,
];

export const WithinOperators = [
  TimestampOperator.Within,
  TimestampOperator.NotWithin,
];

export const AbsoluteRelativeTimestampOperators = [
  TimestampOperator.Equals,
  TimestampOperator.Before,
  TimestampOperator.After,
  TimestampOperator.Between,
  TimestampOperator.BetweenInclusive,
];

export const RelativeOnlyTimestampOperators = [
  TimestampOperator.Within,
  TimestampOperator.NotWithin,
];

/**
 * Operators whose SQL is always evaluated relative to the current time.
 * These can't be used in filters that are compiled once and cached
 * (e.g. predictive trait event filters).
 */
export const RelativeTimestampOperators = [
  TimestampOperator.Within,
  TimestampOperator.NotWithin,
  TimestampOperator.Anniversary,
];

export const initialPropertyCondition: PropertyCondition = {
  type: ConditionType.Property,
  propertyType: null,
  property: null,
  operator: null,
  value: null,
  subconditions: [],
};

export const createPropertyCondition = (): PropertyCondition => ({
  ...initialPropertyCondition,
  id: crypto.randomUUID(),
});

export const createRootPropertyCondition = (): {
  type: ConditionType.And;
  id: string;
  conditions: PropertyCondition[];
} => ({
  type: ConditionType.And,
  id: crypto.randomUUID(),
  conditions: [createPropertyCondition()],
});

export const initialEventCondition: EventCondition = {
  type: ConditionType.Event,
  operator: NumberOperator.GreaterThanOrEqualTo,
  value: 1,
  eventModelId: null,
  relationshipId: null,
  subconditions: [],
};

export const initialEventWindow: Window = {
  operator: TimestampOperator.Within,
  timeType: TimeType.Relative,
  value: {
    interval: IntervalUnit.Day,
    quantity: 7,
    direction: RelativeDirection.Backward,
  },
  propertyOptions: {
    timestampGranularity: TimestampGranularity.Full,
  },
};

export const initialRealtimeEventWindow: Window = {
  operator: TimestampOperator.Within,
  timeType: TimeType.Relative,
  value: {
    interval: IntervalUnit.Hour,
    quantity: 24,
    direction: RelativeDirection.Backward,
  },
};

export const initialNumberOfCondition: NumberOfCondition = {
  type: ConditionType.NumberOf,
  relationshipId: null,
  operator: NumberOperator.GreaterThanOrEqualTo,
  value: 1,
  subconditions: [],
};

export const initialSetCondition: SegmentSetCondition = {
  type: ConditionType.SegmentSet,
  modelId: null,
  includes: true,
};

export const initialJourneySetCondition: JourneySetCondition = {
  type: ConditionType.JourneySet,
  journeyIds: [],
  filter: {
    condition: "in-journey",
    evalWindow: undefined,
  },
};

export const initialFunnelCondition: FunnelCondition = {
  type: ConditionType.Funnel,
  eventModelId: null,
  relationshipId: null,
  didPerform: false,
  subconditions: [],
};

export const initialFunnelWindow: Window = {
  operator: TimestampOperator.Within,
  timeType: TimeType.Relative,
  value: {
    interval: IntervalUnit.Day,
    quantity: 7,
    direction: RelativeDirection.Forward,
  },
  propertyOptions: { timestampGranularity: TimestampGranularity.Full },
};

export const MultiValueColumnTypes = [
  ColumnType.String,
  ColumnType.Number,
  ColumnType.JsonArrayStrings,
  ColumnType.JsonArrayNumbers,
];

export const MultiValueOperatorsByColumnType = {
  [ColumnType.String]: [
    StringOperator.Equals,
    StringOperator.DoesNotEqual,
    StringOperator.Contains,
    StringOperator.DoesNotContain,
  ],
  [ColumnType.Number]: [NumberOperator.Equals, NumberOperator.DoesNotEqual],
  [ColumnType.JsonArrayStrings]: [JsonArrayOperator.ContainsLike],
  [ColumnType.JsonArrayNumbers]: [JsonArrayOperator.ContainsLike],
};

export const ColumnComparisonOperatorsByColumnType = {
  [ColumnType.String]: [StringOperator.Equals, StringOperator.DoesNotEqual],
  [ColumnType.Number]: [
    NumberOperator.Equals,
    NumberOperator.DoesNotEqual,
    NumberOperator.GreaterThan,
    NumberOperator.GreaterThanOrEqualTo,
    NumberOperator.LessThan,
    NumberOperator.LessThanOrEqualTo,
  ],
  [ColumnType.BigInt]: [NumberOperator.Equals, NumberOperator.DoesNotEqual],
  [ColumnType.Boolean]: [BooleanOperator.Equals, BooleanOperator.DoesNotEqual],
  [ColumnType.JsonArrayStrings]: [
    JsonArrayOperator.Contains,
    JsonArrayOperator.DoesNotContain,
  ],
  [ColumnType.JsonArrayNumbers]: [
    JsonArrayOperator.Contains,
    JsonArrayOperator.DoesNotContain,
  ],
  // Timestamp is managed inside TimestampInput
};

type OperatorsWithMultiValues =
  | StringOperator
  | NumberOperator
  | JsonArrayOperator;

export const MultiValueOperators: OperatorsWithMultiValues[] = Object.values(
  MultiValueOperatorsByColumnType,
).flatMap((operators: OperatorsWithMultiValues[]) => operators);

export const PercentileOperators = [
  NumberOperator.Equals,
  NumberOperator.DoesNotEqual,
  NumberOperator.LessThan,
  NumberOperator.LessThanOrEqualTo,
  NumberOperator.GreaterThan,
  NumberOperator.GreaterThanOrEqualTo,
];

export const FunnelValueOptions = [
  {
    label: "value",
    value: ConditionType.Property,
  },
  {
    label: "property",
    value: ConditionType.ReferenceProperty,
  },
];

export const TraitTypeOptions = [
  {
    label: "Sum",
    value: TraitType.Sum,
  },
  { label: "Count", value: TraitType.Count },
  {
    label: "Average",
    value: TraitType.Average,
  },
  {
    label: "Most frequent",
    value: TraitType.MostFrequent,
  },
  {
    label: "Least frequent",
    value: TraitType.LeastFrequent,
  },
  { label: "First", value: TraitType.First },
  {
    label: "Last",
    value: TraitType.Last,
  },
  {
    label: "SQL",
    value: TraitType.RawSql,
  },
];

export type AdditionalColumn = {
  alias: string;
  column: RelatedColumn | TransformedColumn | StaticValueColumn;
};

export interface TraitCondition extends PropertyCondition {
  property: {
    type: "related";
    path: string[];
    column: TraitColumn;
  };
}

export const getInitialTraitColumn = (trait: {
  id: string;
  type: string; // This is TraitType
  config: TraitConfig;
  relationship: {
    id: string;
  } | null;
}): RelatedColumn | TransformedColumn => {
  if (trait.type === TraitType.TrackedAudienceMembership) {
    // No relationship: the backend reads the audience_membership changelog
    // directly, so the column carries an empty join path (mirrors helpers.ts
    // getReferencedTraits and listSyncableColumns).
    return {
      type: "related",
      path: [],
      column: {
        type: "trait",
        traitDefinitionId: trait.id,
        conditions: [],
      },
    };
  }
  if (trait.type !== TraitType.Formula && trait.relationship) {
    return {
      type: "related",
      path: [String(trait.relationship.id)],
      column: {
        type: "trait",
        traitDefinitionId: trait.id,
        // Trait conditions that aren't directly instantiated from trait templates do not support inline trait conditions
        // They will only use the config.conditions on the trait itself
        conditions: [],
      },
    };
  } else {
    return {
      type: "transformed",
      column: {
        type: "trait",
        traitDefinitionId: trait.id,
        conditions: [],
      },
    };
  }
};

export interface InlineAggregatedTraitCondition extends PropertyCondition {
  property: {
    type: "related";
    path: string[];
    column: InlineAggregatedTrait;
  };
}

interface FormulaTraitCondition extends PropertyCondition {
  property: {
    type: "transformed";
    column: TraitColumn;
  };
}

interface InlineFormulaTraitCondition extends PropertyCondition {
  property: {
    type: "transformed";
    column: InlineFormulaTrait;
  };
}

export interface InlineTraitCondition extends PropertyCondition {
  property:
    | InlineAggregatedTraitCondition["property"]
    | InlineFormulaTraitCondition["property"];
}

export const isColumnReference = (
  property: unknown,
): property is ColumnReference => {
  return typeof property === "object";
};

export {
  isInlineTraitColumn,
  isJourneyEventColumn,
  isRelatedJourneyEventColumn,
  isMergedColumn,
  isRawColumn,
  isRelatedColumn,
  isStaticValueColumn,
  isTraitColumn,
  isJourneyMemberStateColumn,
  isTransformedColumn,
  isSyntheticColumn,
};

export const isPropertyCondition = (
  condition: any,
): condition is PropertyCondition => condition?.type === ConditionType.Property;

export const isTraitCondition = (
  condition: Condition,
): condition is TraitCondition =>
  condition.type === ConditionType.Property &&
  isRelatedColumn(condition.property) &&
  isTraitColumn(condition.property.column);

export const isFormulaTraitCondition = (
  condition: Condition,
): condition is FormulaTraitCondition =>
  condition.type === ConditionType.Property &&
  isTransformedColumn(condition.property) &&
  condition.property.column.type === "trait";

export const isInlineTraitCondition = (
  condition: Condition,
): condition is InlineTraitCondition =>
  condition.type === ConditionType.Property &&
  isRelatedColumn(condition.property) &&
  isInlineTraitColumn(condition.property.column);

export const isInlineAggregatedTraitCondition = (
  condition: Condition,
): condition is InlineAggregatedTraitCondition => {
  return (
    isInlineTraitCondition(condition) &&
    condition.property.column.traitType !== TraitType.Formula
  );
};

/**
 * Returns `true` if `newOperator` cannot suppport the same list of time types as the `oldOperator`
 */
export const shouldResetTimeType = (
  oldOperator: Operator,
  newOperator: Operator,
): boolean => {
  return (
    AbsoluteRelativeTimestampOperators.includes(oldOperator) &&
    RelativeOnlyTimestampOperators.includes(newOperator)
  );
};

/**
 * When using the Between operator, the value is of type `TimeRange` (supports relative and absolute).
 * All other timestamp operators use the value type `IntervalValue` (for relative) or `string` (for absolute).
 * So if the operator transitioned to or from Between, we know its value type has changed.
 */
export const isNewTimeValueTypeDifferent = (
  oldOperator: Operator,
  newOperator: Operator,
): boolean => {
  return (
    [oldOperator, newOperator].includes(TimestampOperator.Between) ||
    [oldOperator, newOperator].includes(TimestampOperator.BetweenInclusive)
  );
};

/**
 * Tests for all conditions where the value type has changed due to the change in operator.
 * If the value _type_ has changed, then we should reset the value.
 */
export const shouldResetValue = ({
  columnType,
  oldValue,
  oldOperator,
  newOperator,
}: {
  columnType: ColumnType | null;
  oldValue: any;
  oldOperator: Operator;
  newOperator: Operator;
}) => {
  const isOldOperatorMultiValue =
    columnType &&
    MultiValueOperatorsByColumnType[columnType]?.includes(oldOperator);
  const isNewOperatorTypeMultiValue =
    columnType &&
    MultiValueOperatorsByColumnType[columnType]?.includes(newOperator);

  const changedFromIntervalToNonIntervalOperator =
    IntervalOperators.includes(oldOperator) &&
    !IntervalOperators.includes(newOperator);

  const isOperatorWithoutValue = OperatorsWithoutValue.includes(newOperator);

  const columnComparisonOperators =
    columnType && ColumnComparisonOperatorsByColumnType[columnType];
  const lostColumnCompareSupport =
    columnComparisonOperators &&
    oldValue?.type === ValueType.Column &&
    columnComparisonOperators.includes(oldOperator) &&
    !columnComparisonOperators.includes(newOperator);

  return (
    isOldOperatorMultiValue !== isNewOperatorTypeMultiValue ||
    lostColumnCompareSupport ||
    changedFromIntervalToNonIntervalOperator ||
    isOperatorWithoutValue ||
    isNewTimeValueTypeDifferent(oldOperator, newOperator)
  );
};

export const shouldResetPercentile = (newOperator: any): boolean => {
  return !PercentileOperators.includes(newOperator);
};

export enum PropertyComparisonType {
  /** Newer default type of property comparison that applies to most fields */
  Default = "col-compare",
  /**
   * Legacy property comparison for journey query builder events.
   * Specifically, event conditions in the journey query builder can compare (only) to entry event columns.
   */
  JourneyEntryEvent = "journey-entry-event",
  /**
   * Legacy property comparison exists for funnel event filters,
   * so need a way to disable the new one
   */
  None = "none",
}
